import api from 'api'
import axios, { AxiosRequestConfig } from 'axios'

import { getBinaryFileContentBySignedUrl, getFileContentBySignedUrl } from 'services'
import type {
  BulkAssignExportRequestsParams,
  BulkAssignExportRequestsResponse,
  BulkUnassignExportRequestsParams,
  BulkUnassignExportRequestsResponse,
  CreateResultLabelParams,
  DeleteResultLabelParams,
  GetAssignableAssigneesResponse,
  GetLabelsFromExportRequestsResponse,
  GetRequestersFromExportRequestsResponse,
  GetResultsVerificationAssigneesResponse,
  GetWorkspacesFromExportRequestsResponse,
  UpdateApproveResultParams
} from 'services/results/types'
import { getAirlockBeUrl } from 'utils/env'

import type {
  GetAutomatedChecksReportResponse,
  GetBinaryFileContentArgs,
  GetBinaryFileContentResponse,
  GetFileContentResponse,
  GetParticipantFileSignedUrlParams,
  GetParticipantFileSignedUrlResponse,
  GetSignedUrlForApprovedResultParams,
  GetSignedUrlForApprovedResultResponse,
  GetTextFileContentArgs,
  GetTextFileContentResponse,
  GetVerificationAttachmentSignedUrlParams,
  GetVerificationAttachmentSignedUrlResponse,
  Result
} from './types'

export const DEFAULT_PAGE_SIZE = 10

export const getResultDetails = async (runId: string): Promise<Result> => {
  const { data } = await api.get<Result>(`/results/verification/${runId}`)
  return data
}

export const getAutomatedChecksReport = async (
  resultId: string,
  jobId: string,
  options?: AxiosRequestConfig
): Promise<GetAutomatedChecksReportResponse> => {
  const { data } = await api.get<GetAutomatedChecksReportResponse>(
    `/results/verification/${resultId}/auto-checks/${jobId}/report`,
    options
  )

  return data
}

export const listBucketContents = async (runId: string, bucket: string, prefix: string): Promise<S3BucketResponse> => {
  const { data } = await api.get<S3BucketResponse>(`/results/verification/${runId}/listBucketContents`, {
    params: {
      bucket,
      prefix
    }
  })
  return data
}

export const getFileContent = async ({
  runId,
  bucket,
  fileKey,
  fetchSignedUrlOnly = false
}: GetTextFileContentArgs): Promise<GetTextFileContentResponse> => {
  const response = (
    await api.get<GetTextFileContentResponse>(`/results/verification/${runId}/fileContent`, {
      params: {
        prefix: fileKey,
        ...(bucket ? { bucket } : {})
      }
    })
  ).data

  if (fetchSignedUrlOnly) return response

  response.fileContent = await getFileContentBySignedUrl(response.signedUrl)
  return response
}

export const getBinaryFileContent = async ({
  runId,
  bucket,
  fileKey,
  fetchSignedUrlOnly = false
}: GetBinaryFileContentArgs): Promise<GetBinaryFileContentResponse> => {
  const response = (
    await api.get<GetBinaryFileContentResponse>(`/results/verification/${runId}/fileContent`, {
      params: {
        prefix: fileKey,
        ...(bucket ? { bucket } : {})
      }
    })
  ).data

  if (fetchSignedUrlOnly) return response

  response.fileContent = await getBinaryFileContentBySignedUrl(response.signedUrl)
  return response
}

export const rejectResult = async (resultId: string, reason: string): Promise<GetFileContentResponse> => {
  const { data } = await api.put<GetFileContentResponse>(`/results/verification/${resultId}/reject`, { reason })
  return data
}

export const requestAttestation = async (resultId: string): Promise<void> => {
  await api.put(`/results/verification/${resultId}/request-attestation`)
}

export const approveResult = async (
  resultId: string,
  params: UpdateApproveResultParams
): Promise<GetFileContentResponse> => {
  const { data } = await api.put<GetFileContentResponse>(`/results/verification/${resultId}/approve`, { ...params })
  return data
}

export const getSignedUrlForApprovedResult = async ({ resultId, token }: GetSignedUrlForApprovedResultParams) => {
  const { data } = await axios.get<GetSignedUrlForApprovedResultResponse>(
    `${getAirlockBeUrl()}/results/${resultId}/access`,
    {
      params: { token }
    }
  )
  return data
}

export const getVerificationAttachmentSignedUrl = async ({
  resultId,
  fieldId,
  attachmentIndex
}: GetVerificationAttachmentSignedUrlParams): Promise<GetVerificationAttachmentSignedUrlResponse> => {
  const { data } = await api.get<GetVerificationAttachmentSignedUrlResponse>(
    `/results/verification/${resultId}/attachments/${fieldId}/${attachmentIndex}/signed-url`
  )

  return data
}

/**
 * Get a short-lived signed URL for the participant ID file of a restricted data access request.
 * The storage location is resolved server-side, so no bucket or prefix is sent or returned.
 * Responds with 409 while the file is still being prepared or its preparation failed.
 */
export const getParticipantFileSignedUrl = async ({
  resultId
}: GetParticipantFileSignedUrlParams): Promise<GetParticipantFileSignedUrlResponse> => {
  const { data } = await api.get<GetParticipantFileSignedUrlResponse>(
    `/results/verification/${resultId}/participant-file/signed-url`
  )

  return data
}

export const getWorkspacesFromExportRequests = async (
  overrides?: AxiosRequestConfig
): Promise<GetWorkspacesFromExportRequestsResponse> => {
  const { data } = await api.get<GetWorkspacesFromExportRequestsResponse>('/results/filters/workspaces', overrides)
  return data
}

/**
 * Get all labels used in export requests.
 * @param overrides - Axios request config.
 * @returns A list of labels used in export requests.
 */
export const getLabelsFromExportRequests = async (
  overrides?: AxiosRequestConfig
): Promise<GetLabelsFromExportRequestsResponse> => {
  const { data } = await api.get<GetLabelsFromExportRequestsResponse>('/results/filters/labels', overrides)
  return data
}

/**
 * Get all users who have made export requests.
 * @param overrides - Axios request config.
 * @returns A list of users who have made export requests.
 */
export const getRequestersFromExportRequests = async (
  overrides?: AxiosRequestConfig
): Promise<GetRequestersFromExportRequestsResponse> => {
  const { data } = await api.get<GetRequestersFromExportRequestsResponse>('/results/filters/users', overrides)
  return data
}

export const bulkAssignExportRequests = async (params: BulkAssignExportRequestsParams) => {
  const { data } = await api.put<BulkAssignExportRequestsResponse>('/results/bulk-assign', params)
  return data
}

export const bulkUnassignExportRequests = async (params: BulkUnassignExportRequestsParams) => {
  const { data } = await api.put<BulkUnassignExportRequestsResponse>('/results/bulk-unassign', params)
  return data
}

export const getAssignableAssignees = async (
  resultId: string,
  options?: AxiosRequestConfig
): Promise<GetAssignableAssigneesResponse> => {
  const { data } = await api.get<GetAssignableAssigneesResponse>(`/results/${resultId}/assignable-assignees`, options)
  return data
}

export const getResultsVerificationAssignees = async (): Promise<GetResultsVerificationAssigneesResponse> => {
  const { data } = await api.get<GetResultsVerificationAssigneesResponse>('/results/filters/assignees')
  return data
}

export const createResultLabel = async (resultId: string, params: CreateResultLabelParams): Promise<void> => {
  await api.post(`/results/${resultId}/labels`, params)
}

export const deleteResultLabel = async (resultId: string, { labelId }: DeleteResultLabelParams): Promise<void> => {
  await api.delete(`/results/${resultId}/labels/${labelId}`)
}
